// Copyright 2025, University of Maryland, All Rights Reserved
import type { JSONSchema } from '@/api/types/schema'

/**
 * Extracts min/max values from the monitor schema's filter_score field
 */
export function getFilterScoreRange(schema?: JSONSchema): { min: number; max: number; step: number } {
  // Default fallback values
  const defaultRange = { min: 0, max: 10, step: 1 }
  
  if (!schema?.properties) {
    return defaultRange
  }
  
  // Look for summary section
  const summarySection = schema.properties.summary
  if (!summarySection?.properties) {
    return defaultRange
  }
  
  // Look for filter_score field in summary section
  const filterScoreField = summarySection.properties.filter_score
  if (!filterScoreField?.properties) {
    return defaultRange
  }
  
  // Look for score property within filter_score
  const scoreProperty = filterScoreField.properties.score
  if (!scoreProperty || scoreProperty.type !== 'integer') {
    return defaultRange
  }
  
  // Extract min/max values
  const min = scoreProperty.minimum ?? 0
  const max = scoreProperty.maximum ?? 10
  
  // Calculate appropriate step based on range
  const range = max - min
  let step = 1
  
  if (range > 1000000) {
    step = 10000  // For very large ranges like 1 billion
  } else if (range > 100000) {
    step = 1000   // For ranges over 100k
  } else if (range > 10000) {
    step = 100    // For ranges over 10k
  } else if (range > 1000) {
    step = 10     // For ranges over 1k
  } else if (range > 100) {
    step = 5      // For ranges over 100
  }
  // else step = 1 for smaller ranges
  
  return { min, max, step }
}

/**
 * Formats a score value for display based on its magnitude
 */
export function formatScoreValue(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`
  }
  return value.toString()
}

/**
 * Generates step labels for the slider based on the range
 */
export function generateStepLabels(min: number, max: number): Array<{ value: number; label: string }> {
  const range = max - min
  const steps = []
  
  if (range <= 100) {
    // For small ranges, show min, middle, max
    steps.push({ value: min, label: min.toString() })
    if (range > 2) {
      const middle = Math.round((min + max) / 2)
      steps.push({ value: middle, label: middle.toString() })
    }
    steps.push({ value: max, label: max.toString() })
  } else {
    // For large ranges, show formatted values
    steps.push({ value: min, label: formatScoreValue(min) })
    const middle = Math.round((min + max) / 2)
    steps.push({ value: middle, label: formatScoreValue(middle) })
    steps.push({ value: max, label: formatScoreValue(max) })
  }
  
  return steps
}
